import type { ReactNode } from "react";
import { SchemaDiagram } from "../../lib/funnel/SchemaDiagram";
import { SIGNUP_FUNNEL_TABLES, SIGNUP_FUNNEL_JOINS } from "./schemaConfig";

type Block = { title: string; body: ReactNode };

const BLOCKS: Block[] = [
  {
    title: "cohort",
    body: (
      <p>
        Open all-time window. Cohort filtering is applied client-side via the page filter bar so the SQL stays cacheable
        across cohort selections.
      </p>
    ),
  },
  {
    title: "transcripts / synopses",
    body: (
      <p>
        Per (user × company) counts and first/last dates. Both join through <code>public.meeting</code> and exclude
        <code>is_demo = true</code> and <code>is_deleted = true</code> meetings. Preloaded demo content silently inflates these
        stages by ~830 users if the filter is dropped. <code>meeting_synopsis</code> has no <code>is_deleted</code> column;
        the meeting-side filter is the only soft-delete gate.
      </p>
    ),
  },
  {
    title: "chats",
    body: (
      <p>
        Single CTE with two <code>FILTER (WHERE …)</code> aggregates: <code>chat_meeting_count</code> for chats keyed on a
        meeting or synopsis, and <code>chat_client_count</code> for chats keyed on a client. The client-keyed Ask Marloo lift
        is the strongest pre-paid predictor in the historical data, so it deserves its own funnel column.
      </p>
    ),
  },
  {
    title: "custom_templates",
    body: (
      <p>
        User-created templates older than 5 minutes after sign-up. The 5-minute threshold excludes auto-provisioned
        templates that get created at sign-up time; they carry no <code>template_default_external_id</code> link, so a
        time-based gate is the only reliable split.
      </p>
    ),
  },
  {
    title: "calendar_first",
    body: (
      <p>
        First <code>public.calendar</code> row per (user, company). The <code>integration</code> table doesn't carry a
        <code>created_date_utc</code> and its enum doesn't include a calendar value, so we use the calendar table directly
        (the first row appears moments after the user's first calendar sync, which is a good enough proxy for connect-time).
      </p>
    ),
  },
  {
    title: "company_size",
    body: (
      <p>
        Distinct active <code>user_roles</code> per company. <code>user_roles</code> uses a status enum (<code>active</code> /
        <code>inactive</code>), not an <code>is_active</code> boolean.
      </p>
    ),
  },
  {
    title: "billing",
    body: (
      <p>
        Per-company lifetime paid status: <code>on_paid_plan</code> is true if any billing row on the company is plus or pro.
        The earliest qualifying <code>subscription_started_date_utc</code> is kept. <code>billing_plan</code> is the most
        recent plan.
      </p>
    ),
  },
  {
    title: "demo_attendance",
    body: (
      <p>
        Per attendee email, lifetime had-demo flag and earliest demo date. Sourced from <code>reporting.demo</code> with
        <code>follow_up_context.is_demo = 'true'</code>. Pre-Feb 2026 history is being backfilled directly into
        <code>reporting.demo</code> rather than via a separate fallback table. Demos attach to people, so the same flag
        appears on every (user, company) row for that user.
      </p>
    ),
  },
  {
    title: "Final projection",
    body: (
      <p>
        One row per <code>(user, company)</code>. Activity columns attribute to the workspace where the activity happened.
        UTM data lives in <code>users.metadata</code> jsonb (not as columns); <code>fbc</code> and <code>gclid</code> are
        columns on <code>users</code>. Cohort filtering and the demo-vs-self split are applied client-side; KPIs / butterfly
        / cohort grid all use the filter-then-distinct collapse rule (filter the per-pair rowset, then count distinct users).
      </p>
    ),
  },
];

export function SignupFunnelExplain() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ margin: 0, color: "var(--fg-2)" }}>
        The Sign-up Activation dashboard runs a single SQL query whose spine is <code>public.user_roles</code> (status='active').
        It returns one row per <code>(user, company)</code> pair. Sections below describe each CTE.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {BLOCKS.map((b) => (
          <div
            key={b.title}
            style={{
              background: "var(--bg-subtle, #FAFAF7)",
              border: "1px solid var(--border-default)",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace", color: "var(--color-teal)", marginBottom: 6 }}>{b.title}</div>
            <div style={{ fontSize: 12, color: "var(--fg-1)", lineHeight: 1.5 }}>{b.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SignupFunnelSchema() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ margin: 0, color: "var(--fg-2)" }}>
        Tables and CTEs touched by this dashboard. Click any node to collapse / expand its columns; drag nodes to rearrange.
        Search filters table and column names.
      </p>
      <SchemaDiagram tables={SIGNUP_FUNNEL_TABLES} joins={SIGNUP_FUNNEL_JOINS} height={680} />
    </div>
  );
}
